/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { Error, ErrorIcon, ErrorTitle, ErrorDescription } from '@quadminds/mindkit';
import IconDark from '@quadminds/mindy-app/assets/img/errors/UserDark.svg';
import IconLight from '@quadminds/mindy-app/assets/img/errors/UserLight.svg';
import useGetUserInfo from '@quadminds/mindy-app/hooks/useGetUserInfo';
import { getAuthService } from '@quadminds/mindy-app/services/auth.service';
import { TypologiesEnum } from '@quadminds/shared/auth-system';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { getUsersLandingUrl } from './../../../config';
import { requiresApiKey } from '../../../utils/utils';

export function UserRestricted() {
    const { t } = useTranslation();
    const [retry, setRetry] = useState(true);
    const [redirected, setRedirected] = useState(false);
    const queryClient = useQueryClient();
    const { data: userInfo } = useGetUserInfo();
    const navigate = useNavigate();
    const location = useLocation();

    // Typology and API key checks
    const hasUserTypologies = [TypologiesEnum.USER, TypologiesEnum.SUBUSER].includes(
        userInfo?.typology as TypologiesEnum,
    );
    const thereIsAProblemWithUserApiKey =
        requiresApiKey() &&
        (hasUserTypologies ? userInfo?.personalApiKey === undefined : userInfo?.apiKey === undefined);

    // Mindy app access restriction
    const isRestrictedDueToApp =
        location.pathname === '/error-out-scope/restricted-access' &&
        userInfo &&
        !userInfo.applications?.includes?.('mindy');

    useEffect(() => {
        if (userInfo && thereIsAProblemWithUserApiKey && retry && !isRestrictedDueToApp) {
            queryClient.refetchQueries({ queryKey: ['userInfo'] });
            setRetry(false);
        }
    }, [userInfo, thereIsAProblemWithUserApiKey, retry, queryClient, isRestrictedDueToApp]);

    useEffect(() => {
        if (userInfo?.personalApiKey && !isRestrictedDueToApp && !redirected) {
            setRedirected(true);
            navigate('/chat');
        }
    }, [userInfo, isRestrictedDueToApp, navigate, redirected]);

    if (userInfo === undefined) {
        return <Navigate to="/error-out-scope/user-not-found" />;
    }

    // Error message selection
    const errorMessages: JSX.Element[] = [<p key="desc1">{t('errors.restrictedAccessDescription')}</p>];

    if (thereIsAProblemWithUserApiKey) {
        errorMessages.push(
            <p key="no-api" className="mt-2 text-yellow-600">
                {t('errors.missingApiKeyMessage')}
            </p>,
        );
    }

    return (
        <Error>
            <ErrorIcon>
                <img src={IconLight} alt={t('errors.iconAlt')} className="dark:hidden h-24 w-auto m-5" />
                <img src={IconDark} alt={t('errors.iconAlt')} className="hidden dark:block h-24 w-auto m-5" />
            </ErrorIcon>
            <ErrorTitle>{t('errors.restrictedAccess')}</ErrorTitle>
            <ErrorDescription>
                {errorMessages}
                <div className="mt-4 flex flex-col justify-center items-center gap-2">
                    <button
                        className="max-w-30 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition"
                        onClick={() => {
                            getAuthService()
                                .logout()
                                .then(() => {
                                    queryClient.clear();
                                    localStorage.clear();
                                    globalThis.location.assign(
                                        `${getUsersLandingUrl()}/login?logout=true&redirect=${
                                            globalThis.location.href
                                        }`,
                                    );
                                });
                        }}
                        aria-label={t('header.userMenu.logout')}
                        type="button"
                    >
                        {t('header.userMenu.logout')}
                    </button>
                </div>
            </ErrorDescription>
        </Error>
    );
}

export default UserRestricted;
